import { loadThemeFromStorage } from "@/lib/theme";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import "./i18n/index.ts";
import * as TanStackQueryProvider from "./integrations/tanstack-query/root-provider";

import { routeTree } from "./routeTree.gen";

import ErrorBoundary from "./components/common/ErrorBoundary";
import NotFoundPage from "./components/common/NotFound";
import PwaPrompt from "./components/common/PwaPrompt";
import { InlineLoader, SplashLoader } from "./components/loading";
import { AuthProvider, useAuth } from "./providers/AuthProvider";
import reportWebVitals from "./reportWebVitals";

import type { BreadcrumbValue } from "./components/layout/Breadcrumb.tsx";
import "./styles.css";

loadThemeFromStorage();

export const router = createRouter({
  routeTree,
  context: {
    ...TanStackQueryProvider.getContext(),
    auth: undefined!,
  },
  defaultViewTransition: true,
  defaultPreload: "intent",
  scrollRestoration: true,
  getScrollRestorationKey: (location) => location.pathname,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
  defaultNotFoundComponent: NotFoundPage,
  defaultErrorComponent: ErrorBoundary,
  defaultHashScrollIntoView: true,
  defaultPendingComponent: () => <InlineLoader />,
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
  interface StaticDataRouteOption {
    breadcrumb?: BreadcrumbValue;
  }
}

function RouterWithAuth() {
  const auth = useAuth();

  if (auth.isLoading) {
    return <SplashLoader />;
  }

  return (
    <RouterProvider
      router={router}
      context={{
        ...TanStackQueryProvider.getContext(),
        auth,
      }}
    />
  );
}

function App() {
  return (
    <TanStackQueryProvider.Provider>
      <AuthProvider>
        <RouterWithAuth />
        <PwaPrompt />
      </AuthProvider>
    </TanStackQueryProvider.Provider>
  );
}

const rootElement = document.getElementById("app");
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
